import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { LegalNoticeText } from "src/components/LegalNotice";

const SectionHeading = ({ children }: { children: string }) => (
  <Typography variant="h4" color="primary" textAlign="center">
    {children}
  </Typography>
);

const SubHeading = ({ children }: { children: string }) => (
  <Typography
    variant="body2"
    component="h3"
    fontSize="inherit"
    lineHeight="inherit"
    fontWeight={700}
  >
    {children}
  </Typography>
);

export const TosPreamble = () => {
  return (
    <Stack gap={5}>
      <Box display="flex" flexDirection="column" gap={2}>
        <SectionHeading>SUMMARY OF TERMS</SectionHeading>
        <LegalNoticeText>
          Among other things, the Terms provide that you must:
        </LegalNoticeText>
        <ul>
          <li>
            be of the age of majority in your jurisdiction (and in any event be
            at least eighteen years of age), of sound mental capacity and have
            all technical knowledge necessary or advisable to understand and
            evaluate the risks of Terminal and Third Party Protocols;
          </li>
          <li>
            agree that Terminal is provided for informational purposes only and
            is not directly or indirectly in control of or capable of
            interacting with the Blockchain network or performing or effecting
            any transactions on your behalf;
          </li>
          <li>
            agree that Terminal is only being provided as an aid to your own
            independent research and evaluation of Third Party Protocols and
            that no representation or warranty is being made as to the accuracy
            or completeness of information on Terminal;
          </li>
          <li>
            agree that the ability of Terminal to connect with third-party
            wallet applications or devices is not an endorsement or
            recommendation thereof by or on behalf of Terminal Operator, and
            you must assume all responsibility for selecting and evaluating and
            incurring the risks of any bugs, defects, malfunctions or
            interruptions of any third-party wallet applications or devices you
            directly or indirectly use in connection with Terminal;
          </li>
          <li>comply with all applicable laws, rules and regulations;</li>
          <li>
            not be a person who is subject to national or international
            sanctions under the laws of the United States, the United Kingdom,
            the British Virgin Islands or any other nation-state or under any
            applicable law;
          </li>
          <li>
            not hold Terminal Operator or any of its representatives or
            affiliates liable for any damages you suffer in connection with
            your use of Terminal, any Third Party Protocols, Smart Contract, or
            third-party dependency or integration;
          </li>
          <li>
            waive your right to initiate or participate in class actions
            relating to Terminal; and
          </li>
          <li>
            resolve any disputes regarding Terminal pursuant to binding,
            confidential arbitration and waive your right to a jury trial in
            connection with such disputes.
          </li>
        </ul>
        <LegalNoticeText>
          The above is only a partial summary. You should read the Terms and
          Conditions in their entirety. In the event of any conflict or
          inconsistency between this summary and the Terms and Conditions
          relating to any issue, the Terms and Conditions will be determinative
          of the issue.
        </LegalNoticeText>
      </Box>

      <Box display="flex" flexDirection="column" gap={2}>
        <SectionHeading>BACKGROUND INFORMATION</SectionHeading>

        <SubHeading>A. About the Site Operator</SubHeading>
        <LegalNoticeText>
          Terminal aggregates and publishes publicly available third-party
          information about, among other things, on the web application
          available at padre.gg and related domains:
        </LegalNoticeText>
        <ul>
          <li>Third Party Protocols;</li>
          <li>third-party tokens;</li>
          <li>Third Party Smart Contract systems;</li>
          <li>
            the Blockchain network to which the foregoing Smart Contract
            systems, tokens, and protocols are deployed; and
          </li>
          <li>Blockchain digital assets related to the foregoing.</li>
        </ul>
        <LegalNoticeText>
          Terminal also offers interaction methods whereby the User can
          indicate a transaction the User would like to perform in connection
          with the Third Party Protocols (such as swapping one digital asset
          for another).
        </LegalNoticeText>
        <LegalNoticeText>
          When used in this way, Terminal can generate a draft transaction
          message which the User can independently use in conjunction with a
          third-party wallet application or device to conduct transactions on
          Blockchain systems or Third Party Smart Contract systems.
        </LegalNoticeText>

        <SubHeading>B. About Terminal</SubHeading>
        <LegalNoticeText>
          Terminal, as defined below, owns and operates the web application
          and associated Smart Contracts available at padre.gg and related
          domains. Terminal owns the terminal brand and brand assets. Terminal
          does not own, operate, or control any of the third party protocols,
          wallet applications, Smart Contracts, digital assets, Blockchain
          tokens or third party intellectual property (collectively
          &ldquo;Third Party Protocols&rdquo;) accessible through the Terminal
          web application and Smart Contracts.
        </LegalNoticeText>

        <SubHeading>C. About Third Party Protocols</SubHeading>
        <LegalNoticeText>
          The Third Party Protocols accessible through the Terminal web
          application and Smart Contracts are software source code freely
          licensed to the public, which provide, among other things,
          third-party wallet application or device or Blockchain node any User
          may pay to operate and record the results of the Third Party
          Protocols in accordance with the User&rsquo;s instructions, thus
          effectuating operations and transactions on a Blockchain network.
        </LegalNoticeText>
        <LegalNoticeText>
          Terminal Operator does not own, operate or control Blockchain
          network, Third Party Smart Contract Systems or any Third Party
          Protocol systems. Using Third Party Smart Contract Systems,
          Blockchain network, or Third Party Protocol systems does not require
          use of Terminal. Terminal aggregates and publishes publicly
          available information about the Third Party Protocol systems in a
          user-friendly and convenient format. Such information is also
          independently available from other sources&mdash;for example, a
          person may directly review Third Party Protocol transaction history,
          account balances and the Third Party Protocol systems on a
          compatible block explorer for such systems.
        </LegalNoticeText>
        <LegalNoticeText>
          By combining publicly available information with the User&rsquo;s
          interactions with Terminal, Terminal can draft standard transaction
          messages compatible with the Third Party Protocol systems which are
          designed to accomplish the User&rsquo;s operational goals as
          expressed through the interactions. If the User so wishes, the User
          may broadcast such messages to the validator network for any
          relevant Blockchain in order to initiate transactions.
        </LegalNoticeText>
        <LegalNoticeText>
          All draft transaction messages are delivered by Terminal via API or
          Terminal&rsquo;s Smart Contracts to a compatible third-party wallet
          application or device selected by the User after pressing the
          &ldquo;Connect Wallet&rdquo; (or similar) button on Terminal. The
          User must personally review and authorize all transaction messages
          that the User wishes to send to Blockchain Systems, Third Party
          Smart Contract Systems or other Blockchain network; this requires
          the User to sign the relevant transaction message with a private
          cryptographic key inaccessible to Terminal. The User&rsquo;s message
          will then be broadcast to the relevant Blockchain through the wallet
          application or device and the User may pay a network fee to have
          the relevant Blockchain network apply the transaction message to
          the Third Party Protocol Smart Contract system and record the
          results as a transaction being completed on that Blockchain.
        </LegalNoticeText>
        <LegalNoticeText>
          Terminal is not an agent or intermediary of the User, does not store
          or have access to or control over any Blockchain digital assets,
          private keys, passwords, accounts or other property of the User, and
          are not capable of performing transactions or sending transaction
          messages on behalf of the User. Terminal does not hold and cannot
          purchase, sell or trade any Blockchain digital assets. All
          transactions relating to the Third Party Protocol systems are
          effected and recorded solely through the interactions of the User
          with the respective Blockchain, who are not under the control of or
          affiliated with Terminal Operator or Terminal.
        </LegalNoticeText>

        <SubHeading>D. Disclaimers and Disclosures</SubHeading>
        <LegalNoticeText>
          Important disclaimers and disclosures regarding the subject material
          of Terminal can be found in these Terms. You should familiarize
          yourself with these the Terms and conduct your own thorough due
          diligence into the Third Party Protocols before using Terminal.
        </LegalNoticeText>
      </Box>

      <SectionHeading>BINDING PROVISIONS</SectionHeading>
    </Stack>
  );
};
